/**
 * PostgreSQL-specific database queries
 */

export const pgsqlQueries = {
  /** Get all databases (excluding templates) */
  databases: `
    SELECT datname AS name
    FROM pg_database
    WHERE datistemplate = false
    ORDER BY datname;
  `,

  /**
   * Schemas a table can be created in, read from `pg_namespace` rather than
   * derived from the table listing: a database with no tables yet still has
   * `public`, and Data Import's Target step must offer it before the first
   * table exists. System namespaces (`pg_catalog`, `pg_toast`, the per-backend
   * `pg_temp_*`) and `information_schema` are excluded.
   */
  schemas: `
    SELECT n.nspname AS name
    FROM pg_namespace n
    WHERE n.nspname !~ '^pg_'
      AND n.nspname <> 'information_schema'
    ORDER BY n.nspname;
  `,

  /**
   * Get tables for a database (public schema by default).
   *
   * Includes both ordinary tables (relkind = 'r') and declarative-partitioned
   * parents (relkind = 'p', PG10+), but excludes partition CHILD tables
   * (`c.relispartition`) — those appear under their parent's Partitions folder,
   * not at top level. `relkind` is exposed as a new field so the tree can tell a
   * partitioned parent apart and attach the Partitions subfolder; the existing
   * id/name/schema_name/object_type shape is unchanged, so every other consumer
   * is unaffected.
   */
  tables: `
    SELECT
      c.oid::text AS id,
      c.relname AS name,
      n.nspname AS schema_name,
      c.relkind AS relkind,
      'table' AS object_type
    FROM
      pg_class c
    JOIN
      pg_namespace n ON c.relnamespace = n.oid
    WHERE
      c.relkind IN ('r', 'p')
      AND NOT c.relispartition
      AND n.nspname NOT IN ('pg_catalog', 'information_schema', 'pg_toast')
    ORDER BY
      n.nspname, c.relname;
  `,

  /** Get views for a database */
  views: `
    SELECT
      c.oid::text AS id,
      c.relname AS name,
      n.nspname AS schema_name,
      'view' AS object_type
    FROM
      pg_class c
    JOIN
      pg_namespace n ON c.relnamespace = n.oid
    WHERE
      c.relkind IN ('v', 'm')
      AND n.nspname NOT IN ('pg_catalog', 'information_schema', 'pg_toast')
    ORDER BY
      n.nspname, c.relname;
  `,

  /**
   * Get SQL property graphs (PostgreSQL 19+; pg_class.relkind = 'g').
   * Returns 0 rows with no error on older servers, so the folder is
   * naturally graceful-empty — no version gating needed.
   */
  propertyGraphs: `
    SELECT
      c.oid::text AS id,
      c.relname AS name,
      n.nspname AS schema_name,
      'property-graph' AS object_type
    FROM
      pg_class c
    JOIN
      pg_namespace n ON c.relnamespace = n.oid
    WHERE
      c.relkind = 'g'
      AND n.nspname NOT IN ('pg_catalog', 'information_schema', 'pg_toast')
    ORDER BY
      n.nspname, c.relname;
  `,

  /**
   * Get functions for a database
   * Note: PostgreSQL doesn't have "stored procedures" in the traditional sense,
   * but it has functions. PostgreSQL 11+ has PROCEDURE but they're still stored in pg_proc.
   */
  storedProcedures: `
    SELECT
      p.oid::text AS id,
      p.proname AS name,
      n.nspname AS schema_name,
      'stored-procedure' AS object_type
    FROM
      pg_proc p
    JOIN
      pg_namespace n ON p.pronamespace = n.oid
    WHERE
      p.prokind = 'p'
      AND n.nspname NOT IN ('pg_catalog', 'information_schema')
    ORDER BY
      n.nspname, p.proname;
  `,

  /** Get functions for a database */
  functions: `
    SELECT
      p.oid::text AS id,
      p.proname AS name,
      n.nspname AS schema_name,
      'function' AS object_type
    FROM
      pg_proc p
    JOIN
      pg_namespace n ON p.pronamespace = n.oid
    WHERE
      p.prokind = 'f'
      AND n.nspname NOT IN ('pg_catalog', 'information_schema')
    ORDER BY
      n.nspname, p.proname;
  `,

  /**
   * Get columns for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  columns: (schema: string, tableName: string) => `
    SELECT
      a.attname AS column_name,
      pg_catalog.format_type(a.atttypid, a.atttypmod) AS data_type,
      -- Native enum signal for the renderer; array-of-enum (typtype 'b') and
      -- domain-over-enum (typtype 'd') are intentionally out of scope.
      (t.typtype = 'e') AS is_enum,
      CASE
        WHEN a.atttypmod > 0 THEN a.atttypmod - 4
        ELSE NULL
      END AS max_length,
      CASE
        WHEN t.typname IN ('numeric', 'decimal') THEN ((a.atttypmod - 4) >> 16) & 65535
        ELSE NULL
      END AS precision,
      CASE
        WHEN t.typname IN ('numeric', 'decimal') THEN (a.atttypmod - 4) & 65535
        ELSE NULL
      END AS scale,
      NOT a.attnotnull AS is_nullable,
      COALESCE(
        (SELECT true FROM pg_attribute pa
         JOIN pg_index pi ON pa.attrelid = pi.indrelid AND pa.attnum = ANY(pi.indkey)
         WHERE pi.indisprimary AND pa.attrelid = a.attrelid AND pa.attnum = a.attnum),
        false
      ) AS is_primary_key,
      COALESCE(pg_get_serial_sequence(quote_ident('${schema.replace(/'/g, "''")}')||'.'||quote_ident('${tableName.replace(/'/g, "''")}'), a.attname) IS NOT NULL, false) AS is_identity,
      pg_get_expr(d.adbin, d.adrelid) AS default_value,
      a.attnum AS ordinal_position
    FROM
      pg_attribute a
    JOIN
      pg_class c ON a.attrelid = c.oid
    JOIN
      pg_namespace n ON c.relnamespace = n.oid
    JOIN
      pg_type t ON a.atttypid = t.oid
    LEFT JOIN
      pg_attrdef d ON a.attrelid = d.adrelid AND a.attnum = d.adnum
    WHERE
      n.nspname = '${schema.replace(/'/g, "''")}'
      AND c.relname = '${tableName.replace(/'/g, "''")}'
      AND a.attnum > 0
      AND NOT a.attisdropped
    ORDER BY
      a.attnum;
  `,

  /**
   * Get table constraints for a table
   * Uses WITH ORDINALITY to correctly pair columns with referenced columns
   * for composite foreign keys (avoids cross-product issues with ANY())
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableConstraints: (schema: string, tableName: string) => `
    SELECT
      con.conname AS constraint_name,
      CASE con.contype
        WHEN 'p' THEN 'PRIMARY KEY'
        WHEN 'f' THEN 'FOREIGN KEY'
        WHEN 'u' THEN 'UNIQUE'
        WHEN 'c' THEN 'CHECK'
        ELSE con.contype::text
      END AS constraint_type,
      a.attname AS column_name,
      fnsp.nspname AS referenced_schema,
      fcl.relname AS referenced_table,
      fa.attname AS referenced_column,
      pg_get_constraintdef(con.oid) AS check_definition,
      col_ord.ord AS column_ordinal
    FROM
      pg_constraint con
    JOIN
      pg_class cl ON con.conrelid = cl.oid
    JOIN
      pg_namespace nsp ON cl.relnamespace = nsp.oid
    -- Use WITH ORDINALITY to get position-aware unnesting
    LEFT JOIN LATERAL unnest(con.conkey) WITH ORDINALITY AS col_ord(col_num, ord) ON true
    LEFT JOIN
      pg_attribute a ON a.attrelid = con.conrelid AND a.attnum = col_ord.col_num
    LEFT JOIN
      pg_class fcl ON con.confrelid = fcl.oid
    LEFT JOIN
      pg_namespace fnsp ON fcl.relnamespace = fnsp.oid
    -- Match referenced column by same ordinal position
    LEFT JOIN LATERAL unnest(con.confkey) WITH ORDINALITY AS ref_ord(ref_num, ref_ord)
      ON ref_ord.ref_ord = col_ord.ord
    LEFT JOIN
      pg_attribute fa ON fa.attrelid = con.confrelid AND fa.attnum = ref_ord.ref_num
    WHERE
      nsp.nspname = '${schema.replace(/'/g, "''")}'
      AND cl.relname = '${tableName.replace(/'/g, "''")}'
    ORDER BY
      constraint_type, con.conname, col_ord.ord;
  `,

  /**
   * Get table indexes for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableIndexes: (schema: string, tableName: string) => `
    SELECT
      i.relname AS index_name,
      a.attname AS column_name,
      ix.indisunique AS is_unique,
      am.amname AS index_type,
      array_position(ix.indkey, a.attnum) AS key_ordinal
    FROM
      pg_index ix
    JOIN
      pg_class i ON ix.indexrelid = i.oid
    JOIN
      pg_class t ON ix.indrelid = t.oid
    JOIN
      pg_namespace n ON t.relnamespace = n.oid
    JOIN
      pg_am am ON i.relam = am.oid
    JOIN
      pg_attribute a ON a.attrelid = t.oid AND a.attnum = ANY(ix.indkey)
    WHERE
      n.nspname = '${schema.replace(/'/g, "''")}'
      AND t.relname = '${tableName.replace(/'/g, "''")}'
      AND NOT ix.indisprimary
    ORDER BY
      i.relname, array_position(ix.indkey, a.attnum);
  `,

  /**
   * Get default constraints for a table
   * PostgreSQL doesn't have named default constraints, so we synthesize names from column names
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableDefaultConstraints: (schema: string, tableName: string) => `
    SELECT
      'DF_' || a.attname AS constraint_name,
      'DEFAULT' AS constraint_type,
      a.attname AS column_name,
      pg_get_expr(d.adbin, d.adrelid) AS default_definition
    FROM pg_attrdef d
    JOIN pg_attribute a ON d.adrelid = a.attrelid AND d.adnum = a.attnum
    JOIN pg_class c ON d.adrelid = c.oid
    JOIN pg_namespace n ON c.relnamespace = n.oid
    WHERE
      n.nspname = '${schema.replace(/'/g, "''")}'
      AND c.relname = '${tableName.replace(/'/g, "''")}'
      AND a.attnum > 0
      AND NOT a.attisdropped
    ORDER BY a.attname;
  `,

  /**
   * Get table triggers for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableTriggers: (schema: string, tableName: string) => `
    SELECT
      tg.tgname AS trigger_name,
      CASE tg.tgtype & 66
        WHEN 2 THEN 'BEFORE'
        WHEN 64 THEN 'INSTEAD OF'
        ELSE 'AFTER'
      END AS trigger_timing,
      array_to_string(ARRAY[
        CASE WHEN tg.tgtype & 4 > 0 THEN 'INSERT' END,
        CASE WHEN tg.tgtype & 8 > 0 THEN 'UPDATE' END,
        CASE WHEN tg.tgtype & 16 > 0 THEN 'DELETE' END,
        CASE WHEN tg.tgtype & 32 > 0 THEN 'TRUNCATE' END
      ]::text[], ', ') AS trigger_events,
      CASE WHEN tg.tgenabled = 'D' THEN 0 ELSE 1 END AS is_enabled
    FROM pg_trigger tg
    JOIN pg_class c ON tg.tgrelid = c.oid
    JOIN pg_namespace n ON c.relnamespace = n.oid
    WHERE
      n.nspname = '${schema.replace(/'/g, "''")}'
      AND c.relname = '${tableName.replace(/'/g, "''")}'
      AND NOT tg.tgisinternal
    ORDER BY tg.tgname;
  `,

  /**
   * Get table statistics (extended statistics) for a table
   * PostgreSQL extended statistics were introduced in PG 10.
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableStatistics: (schema: string, tableName: string) => `
    SELECT
      s.stxname AS statistic_name,
      a.attname AS column_name,
      cols.ord AS column_ordinal,
      false AS auto_created,
      true AS user_created,
      NULL AS last_updated
    FROM pg_statistic_ext s
    JOIN pg_class c ON s.stxrelid = c.oid
    JOIN pg_namespace n ON c.relnamespace = n.oid
    LEFT JOIN LATERAL unnest(s.stxkeys) WITH ORDINALITY AS cols(col_num, ord) ON true
    LEFT JOIN pg_attribute a ON a.attrelid = c.oid AND a.attnum = cols.col_num
    WHERE
      n.nspname = '${schema.replace(/'/g, "''")}'
      AND c.relname = '${tableName.replace(/'/g, "''")}'
    ORDER BY s.stxname, cols.ord;
  `,

  /**
   * Get the child partitions of a declarative-partitioned parent table.
   * Each row is a direct partition (one level of `pg_inherits`); `partition_bound`
   * carries the human-readable `FOR VALUES …` / `DEFAULT` clause. Returns 0 rows
   * (no error) for a non-partitioned table, so the folder is naturally
   * graceful-empty.
   * @param schema - Parent schema name (will be escaped)
   * @param tableName - Parent table name (will be escaped)
   */
  tablePartitions: (schema: string, tableName: string) => `
    SELECT
      c.oid::text AS id,
      c.relname AS name,
      n.nspname AS schema_name,
      pg_get_expr(c.relpartbound, c.oid) AS partition_bound,
      'partition' AS object_type
    FROM pg_inherits i
    JOIN pg_class c ON c.oid = i.inhrelid
    JOIN pg_namespace n ON n.oid = c.relnamespace
    WHERE i.inhparent = (
      quote_ident('${schema.replace(/'/g, "''")}') || '.' || quote_ident('${tableName.replace(/'/g, "''")}')
    )::regclass
    ORDER BY c.relname;
  `,

  /**
   * Get table-level options + comment + partition definition + inheritance parents.
   * One row expected. Note: PG returns partition info (`partkeydef`) in the same row,
   * so we deliberately do NOT expose a separate `partition` query — the orchestrator
   * populates `metadata.partition` from this row inside `applyTableOptions`.
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableOptions: (schema: string, tableName: string) => `
    SELECT
      c.relpersistence,
      array_to_string(c.reloptions, ',') AS reloptions_text,
      ts.spcname AS tablespace,
      obj_description(c.oid, 'pg_class') AS comment,
      pg_get_partkeydef(c.oid) AS partkeydef,
      (
        SELECT array_agg(pn.nspname || '.' || p.relname ORDER BY i.inhseqno)
        FROM pg_inherits i
        JOIN pg_class p ON p.oid = i.inhparent
        JOIN pg_namespace pn ON pn.oid = p.relnamespace
        WHERE i.inhrelid = c.oid
      ) AS inherits_from
    FROM pg_class c
    JOIN pg_namespace n ON n.oid = c.relnamespace
    LEFT JOIN pg_tablespace ts ON ts.oid = c.reltablespace
    WHERE n.nspname = '${schema.replace(/'/g, "''")}'
      AND c.relname = '${tableName.replace(/'/g, "''")}'
      AND c.relkind IN ('r', 'p');
  `,

  /**
   * Get per-column enrichments (generated expression, collation, comment).
   * Identity seed/increment require deeper sequence introspection — not populated for v1.
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  columnEnrichments: (schema: string, tableName: string) => `
    SELECT
      a.attname AS column_name,
      a.attgenerated,
      pg_get_expr(d.adbin, d.adrelid) AS generation_expression,
      coll.collname AS collation,
      col_description(a.attrelid, a.attnum) AS comment,
      isc.identity_generation,
      isc.identity_start,
      isc.identity_increment
    FROM pg_attribute a
    JOIN pg_class c ON c.oid = a.attrelid
    JOIN pg_namespace n ON n.oid = c.relnamespace
    LEFT JOIN pg_attrdef d ON d.adrelid = a.attrelid AND d.adnum = a.attnum
    LEFT JOIN pg_collation coll ON coll.oid = a.attcollation AND coll.collname <> 'default'
    LEFT JOIN information_schema.columns isc
      ON isc.table_schema = n.nspname AND isc.table_name = c.relname AND isc.column_name = a.attname
    WHERE n.nspname = '${schema.replace(/'/g, "''")}'
      AND c.relname = '${tableName.replace(/'/g, "''")}'
      AND a.attnum > 0
      AND NOT a.attisdropped
    ORDER BY a.attnum;
  `,

  /**
   * Get database properties
   * @param databaseName - Database name (will be escaped)
   */
  databaseProperties: (databaseName: string) => `
    SELECT
      d.datname AS database_name,
      d.datcollate AS collation,
      pg_catalog.pg_get_userbyid(d.datdba) AS owner,
      pg_catalog.pg_encoding_to_char(d.encoding) AS encoding,
      d.datctype AS ctype
    FROM
      pg_database d
    WHERE
      d.datname = '${databaseName.replace(/'/g, "''")}';
  `,

  /** Default database for PostgreSQL */
  defaultDatabase: 'postgres',

  /** Fallback databases if query fails */
  fallbackDatabases: ['postgres'],
};
